/** CMS-485 OASIS prefill field definitions and form application helpers. */

import type { UseFormSetValue } from 'react-hook-form';
import type { Cms485PrefillResponse } from '../services/api/types.gen';
import type { Cms485FormValues } from '../types/cms485.types';
import { listToTextareaValue } from './cms485Form';

export type PrefillMode =
  'all' | 'non-narrative' | 'goals' | 'orders_and_treatments';

/** Narrative fields users often edit manually after an initial OASIS prefill. */
export const NARRATIVE_PREFILL_FIELDS = [
  'goals',
  'orders_and_treatments',
] as const;

const STRING_FIELDS = [
  'soc_date',
  'primary_language',
  'primary_diagnosis_code',
  'primary_diagnosis_description',
  'primary_diagnosis_date',
  'mental_status',
  'dme_supplies',
  'prognosis',
  'safety_measures',
  'nutritional_requirements',
  'medications',
  'orders_and_treatments',
  'goals',
  'risk_profile',
  'homebound_certification_statement',
] as const satisfies readonly (keyof Cms485FormValues)[];

const LIST_TEXTAREA_FIELDS = [
  'functional_limitations',
  'activities_permitted',
] as const satisfies readonly (keyof Cms485FormValues)[];

const ARRAY_ROW_FIELDS = ['secondary_diagnoses', 'allergies'] as const;

export type Cms485PrefillStringField = (typeof STRING_FIELDS)[number];
export type Cms485PrefillListTextareaField =
  (typeof LIST_TEXTAREA_FIELDS)[number];
export type Cms485PrefillArrayRowField = (typeof ARRAY_ROW_FIELDS)[number];
export type Cms485PrefillField =
  | Cms485PrefillStringField
  | Cms485PrefillListTextareaField
  | Cms485PrefillArrayRowField;

export const ALL_PREFILL_FIELDS: readonly Cms485PrefillField[] = [
  ...STRING_FIELDS,
  ...LIST_TEXTAREA_FIELDS,
  ...ARRAY_ROW_FIELDS,
];

const PREFILL_RESPONSE_STRING_KEYS: Record<
  Cms485PrefillStringField,
  keyof Cms485PrefillResponse
> = {
  soc_date: 'soc_date',
  primary_language: 'primary_language',
  primary_diagnosis_code: 'primary_diagnosis_code',
  primary_diagnosis_description: 'primary_diagnosis_description',
  primary_diagnosis_date: 'primary_diagnosis_date',
  mental_status: 'mental_status',
  dme_supplies: 'dme_supplies',
  prognosis: 'prognosis',
  safety_measures: 'safety_measures',
  nutritional_requirements: 'nutritional_requirements',
  medications: 'medications',
  orders_and_treatments: 'orders_and_treatments',
  goals: 'goals',
  risk_profile: 'risk_profile',
  homebound_certification_statement: 'homebound_certification_statement',
};

export function getPrefillFieldsForMode(
  mode: PrefillMode
): readonly Cms485PrefillField[] {
  if (mode === 'goals') {
    return ['goals'];
  }
  if (mode === 'orders_and_treatments') {
    return ['orders_and_treatments'];
  }
  if (mode === 'all') {
    return ALL_PREFILL_FIELDS;
  }
  return ALL_PREFILL_FIELDS.filter(
    field =>
      !NARRATIVE_PREFILL_FIELDS.includes(
        field as (typeof NARRATIVE_PREFILL_FIELDS)[number]
      )
  );
}

export function getPrefillConfirmDescription(mode: PrefillMode): string {
  switch (mode) {
    case 'all':
      return 'Loading from the selected OASIS visit will replace OASIS-derived CMS-485 fields, including the homebound certification statement, and will also refresh Goals (from episode goals) and Orders & Treatments (from the linked SOC visit).';
    case 'non-narrative':
      return 'Loading from the selected OASIS visit will replace non-narrative CMS-485 fields, including the homebound certification statement. Goals and Orders & Treatments will not be changed.';
    case 'goals':
      return 'Loading active goals from this episode will replace the Goals & Outcomes field.';
    case 'orders_and_treatments':
      return 'Loading Orders & Treatments from the linked OASIS SOC visit will replace the current text in this field.';
  }
}

export function isOasisPrefillMode(mode: PrefillMode): boolean {
  return mode === 'all' || mode === 'non-narrative';
}

export function prefillRequiresOasisVisit(mode: PrefillMode): boolean {
  return isOasisPrefillMode(mode) || mode === 'orders_and_treatments';
}

export function getSectionPrefillButtonLabel(
  mode: 'goals' | 'orders_and_treatments'
): string {
  return mode === 'goals'
    ? 'Prefill from goals'
    : 'Prefill from selected visit';
}

export function getPrefillConfirmActionLabel(mode: PrefillMode): string {
  switch (mode) {
    case 'all':
      return 'Prefill all data';
    case 'non-narrative':
      return 'Prefill non-narrative data';
    case 'goals':
      return 'Prefill goals';
    case 'orders_and_treatments':
      return 'Prefill orders & treatments';
  }
}

export function prefillFieldHasContent(
  values: Cms485FormValues,
  field: Cms485PrefillField
): boolean {
  if (field === 'secondary_diagnoses') {
    return values.secondary_diagnoses.some(
      row => row.code.trim() || row.description.trim() || row.date.trim()
    );
  }
  if (field === 'allergies') {
    return values.allergies.some(
      row => row.substance.trim() || row.reaction.trim()
    );
  }
  return Boolean(values[field]?.trim());
}

export function clearPrefillFields(
  setValue: UseFormSetValue<Cms485FormValues>,
  fields: readonly Cms485PrefillField[]
): void {
  for (const field of fields) {
    if (field === 'secondary_diagnoses' || field === 'allergies') {
      setValue(field, [], { shouldDirty: true });
      continue;
    }
    setValue(field, '', { shouldDirty: true });
  }
}

export function applyCms485Prefill(
  setValue: UseFormSetValue<Cms485FormValues>,
  data: Cms485PrefillResponse,
  mode: PrefillMode
): void {
  const fields = new Set(getPrefillFieldsForMode(mode));

  for (const field of STRING_FIELDS) {
    if (!fields.has(field)) continue;
    const value = data[PREFILL_RESPONSE_STRING_KEYS[field]];
    if (typeof value === 'string' && value) {
      setValue(field, value, { shouldDirty: true });
    }
  }

  if (
    fields.has('functional_limitations') &&
    data.functional_limitations?.length
  ) {
    setValue(
      'functional_limitations',
      listToTextareaValue(data.functional_limitations),
      { shouldDirty: true }
    );
  }

  if (fields.has('activities_permitted') && data.activities_permitted?.length) {
    setValue(
      'activities_permitted',
      listToTextareaValue(data.activities_permitted),
      { shouldDirty: true }
    );
  }

  if (fields.has('secondary_diagnoses') && data.secondary_diagnoses?.length) {
    setValue(
      'secondary_diagnoses',
      data.secondary_diagnoses.map(row => ({
        code: row.code ?? '',
        description: row.description ?? '',
        date: row.date ?? '',
      })),
      { shouldDirty: true }
    );
  }

  if (fields.has('allergies') && data.allergies?.length) {
    setValue(
      'allergies',
      data.allergies.map(row => ({
        substance: row.substance ?? '',
        reaction: row.reaction ?? '',
      })),
      { shouldDirty: true }
    );
  }
}

export function hasPrefillContentForMode(
  values: Cms485FormValues,
  mode: PrefillMode
): boolean {
  return getPrefillFieldsForMode(mode).some(field =>
    prefillFieldHasContent(values, field)
  );
}
